import { useQuery } from '@tanstack/react-query';

import { apiService } from '@/services/api-service';

/**
 * Every association folder on the drive, linked or not — the candidate list for the
 * super-admin "Dropbox Path" override (migration 247).
 *
 * ## Why not `useBrowseLevel('Condos')`
 *
 * The column browser intersects the drive with the association roster and HIDES every
 * folder no association claims. That is right for browsing — an unclaimed folder is either
 * a stale rename or a community the viewer may not see — and exactly wrong here, because a
 * folder is a candidate for a pin precisely BECAUSE nothing claims it.
 *
 * ## One query serves the picker and the warning
 *
 * The form needs two answers: "what may I choose" and "does the value I already hold exist
 * on the drive". Both come from this one list, deliberately. A separate existence-check
 * endpoint would eventually disagree with the picker about what counts as a real folder, and
 * a warning that contradicts the dropdown offering the same path is worse than no warning.
 */

export interface DriveFolder {
	/** `<Bucket>/<Folder Name>` — the value stored verbatim, in the drive's own spelling. */
	path: string;
	bucket: string;
	name: string;
	/** The FIRST association resolving here, or null when unclaimed. `associations[0]`. */
	associationId: string | null;
	associationName: string | null;
	/**
	 * Every association filed here. More than one is legitimate — a takeover leaves two
	 * entities sharing a folder — so this is not a collision to warn about, it is a fact to
	 * show. Absent on a response from a server predating this field.
	 */
	associations?: Array<{ id: string; name: string }>;
}

export interface DriveFolderList {
	folders: DriveFolder[];
	count?: number;
	/** Dropbox is not connected — a deployment state, not a fault. */
	unavailable?: 'not-configured';
}

export const documentFolderKeys = {
	all: ['documentFolders'] as const,
};

/**
 * Loaded once and filtered in memory by the picker, per the repo's stable-search-input rule
 * (CLAUDE.md): the list is portfolio-sized (~250 folders across four buckets) and each load
 * costs four Dropbox listings, so re-querying per keystroke would be both slow and a blank
 * screen between characters.
 *
 * `enabled` is the super-admin gate's other half — the endpoint 403s for everyone else, and
 * firing a request that is certain to fail would put an error in every property manager's
 * console on every association edit.
 */
export function useDriveFolders(enabled = true) {
	return useQuery<DriveFolderList>({
		queryKey: documentFolderKeys.all,
		queryFn: () => apiService.fetchApi('/documents/folders') as Promise<DriveFolderList>,
		enabled,
		// Four listings per load, and the answer changes only when someone reorganises the
		// drive. Long enough to open the picker twice without paying twice.
		staleTime: 5 * 60_000,
		retry: false,
	});
}
